import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA} from '@angular/material/legacy-dialog';
import { flatMap, tap } from 'rxjs/operators';
import { getCurrentDateToString, getServerDate, isStartAfterEnd } from '../../shared/helpers/time-helpers.model';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { CustomerCertification } from '../models/customer-certification.model';
import { CustomerType } from '../models/enums/customer-type.enum';
import { CustomerCertificationType } from '../models/enums/customer-certification-type.enum';
import { CustomerManageService } from '../shared/customer-manage.service';
import { CustomerModalBaseComponent } from '../shared/customer-modal-base.component';

export class CertificationContext {
    constructor(public certificationId: number, public customerNumberId: number) {}
}

@Component({
    selector: 'customer-certification-modal',
    templateUrl: './customer-certification-modal.html'
})

export class CustomerCertificationModalComponent extends CustomerModalBaseComponent {
    public certificationId: number;
    public maxDate: Date;
    public certificationForm: UntypedFormGroup;
    public customerCertificationTypes: any[] = (new CustomerCertificationType()).GetActive();
    // Temporary measure to put the certification types in a containing object -- current (as of writing) version of angular is having issues iterating over primitive array not contained in an object
    // TODO: Remove when angular issue resolved
    public certificationTypeContainer =  { customerCertificationTypes: this.customerCertificationTypes };

    // Used when the user is editing an existing certification
    public certificationTypeId: number;
    public certificationTypeDisplayValue: string;

    private certification: CustomerCertification;
    private certificationTypes = new CustomerCertificationType();
    private customerTypes = new CustomerType();

    constructor(
        public location: PlatformLocation,
        public fb: UntypedFormBuilder,
        public service: CustomerManageService,
        public dialogRef: MatDialogRef<CustomerCertificationModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: CertificationContext,
        public modalService: MvdModalService
    ) {
        super(modalService);

        this.certificationId = this.data.certificationId;
        this.certification = new CustomerCertification();

        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    public ngOnInit(): void {
        this.maxDate = new Date();

        this.certificationForm = this.fb.group({
            'customerCertificationTypeId': [null, Validators.required],
            'effectiveDate': [getCurrentDateToString(), Validators.required],
            'expirationDate': [null],
            'description': [''],
            'certificateNumber': [null, Validators.required]
        }, {
            validator: this.dateOrderValidator
        });

        this.getCustomerCertificationTypes();

        if (this.certificationId) {
            this.initCertification();
        }
    }

    private getModel(): CustomerCertification {
        const model = Object.assign(new CustomerCertification(), this.certification);

        model.customerCertificationTypeId = this.certificationForm.get('customerCertificationTypeId').value;
        model.effectiveDate = this.certificationForm.get('effectiveDate').value;
        model.expirationDate = this.certificationForm.get('expirationDate').value;
        model.description = this.certificationForm.get('description').value;
        model.certificateNumber = this.certificationForm.get('certificateNumber').value;

        return model;
    }

    private getCustomerCertificationTypes(): void {
        let customerTypeId: number;
        this.service.getCustomerType(this.data.customerNumberId)
            .subscribe(result => customerTypeId = result,
                e => this.handleValidationResult(e),
                () => {
                    const customerTypeValue = customerTypeId == this.customerTypes.Individual.id ? this.customerTypes.Individual.value : this.customerTypes.Organization.value;
                    this.customerCertificationTypes = this.filterCertifications(customerTypeValue);
                });
    }

    private initCertification(): void {
        this.service.getCustomerCertification(this.certificationId)
            .subscribe(result => this.certification = result,
                e => this.handleValidationResult(e),
                () => {
                    const effectiveDate = getServerDate(this.certification, 'effectiveDate');
                    this.certificationForm.get('effectiveDate').setValue(effectiveDate);
                    const expirationDate = getServerDate(this.certification, 'expirationDate');
                    this.certificationForm.get('expirationDate').setValue(expirationDate);

                    this.certificationForm.get('customerCertificationTypeId').setValue(this.certification.customerCertificationTypeId);
                    this.certificationForm.get('description').setValue(this.certification.description);
                    this.certificationForm.get('certificateNumber').setValue(this.certification.certificateNumber);

                    const certificationType = this.certificationTypes.FromId(this.certification.customerCertificationTypeId);
                    this.certificationTypeId = certificationType.id;
                    this.certificationTypeDisplayValue = certificationType.displayValue;
                });
    }

    private filterCertifications(customerTypeValue: string): any[] {
        return this.customerCertificationTypes.filter(function(certification) {
            return certification.customerCertificationCategoryType == customerTypeValue;
        });
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    public saveCertification(): void {
        if (this.certificationForm.valid) {
            this.showButtonLoading = true;
            this.certification.customerNumberID = this.data.customerNumberId;

            this.service.saveCustomerCertification(this.getModel())
                .subscribe(result => {
                },
                e => {
                    this.showButtonLoading = false;
                    this.handleValidationResult(e);
                },
                () => {
                    this.showButtonLoading = false;
                    this.dialogRef.close(true);
                });
        }
    }

    private dateOrderValidator(group: UntypedFormGroup): ValidationErrors | null {
        const effectiveDate = group.get('effectiveDate').value;
        const expirationDate = group.get('expirationDate').value;

        if (isStartAfterEnd(effectiveDate, expirationDate)) {
            return { 'startAfterEndDate': true };
        }

        return null;
    }

    public deleteCertification(): void {
        const self = this;
        const itemType = this.certificationTypes.FromId(this.certification.customerCertificationTypeId).displayValue;

        this.endDateRecordConfirmation(itemType)
            .pipe(
                tap(() => self.certification.expirationDate = getCurrentDateToString()),
                flatMap(() => self.service.saveCustomerCertification(self.certification))
            )
            .subscribe(
                () => {
                    self.showButtonLoading = false;
                    self.dialogRef.close(true);
                },
                e => {
                    self.showButtonLoading = false;
                    self.handleValidationResult(e);
                }
            );
    }
}
